import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { userLsKey } from "@/lib/userScopedStorage";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  useDraggable,
  useDroppable,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { 
  Wand2, 
  Send, 
  Loader2, 
  Target, 
  CheckSquare, 
  ListTodo, 
  BookOpen, 
  Lightbulb,
  User,
  Bot,
  RefreshCw,
  FileText,
  StickyNote,
  FlaskConical,
  Plus,
  Flag,
  Calendar,
  History,
  Trash2,
  MessageSquare,
  ChevronRight,
  PanelRightClose,
  PanelRightOpen,
  GripVertical,
} from "lucide-react";
import type { Category } from "@shared/schema";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface ConversationState {
  mode: "initial" | "creating_goal" | "creating_habit" | "creating_task" | "creating_journal" | "creating_note" | "learning_system" | "learning_performance";
  step: number;
  collectedData: Record<string, any>;
}

interface PendingMilestone {
  title: string;
  dueDate?: string;
}

interface PendingTask {
  title: string;
  description?: string;
  priority?: string;
  dueDate?: string;
  milestoneIndex?: number;
}

interface PendingGoal {
  title: string;
  progress?: number;
  description?: string;
  targetDate?: string;
  category?: string;
  timeCost?: number;
  timeCostFrequency?: string;
  milestones?: PendingMilestone[];
  tasks?: PendingTask[];
}

interface SavedConversation {
  id: string;
  title: string;
  savedAt: string;
  messages: Array<{ id: string; role: "user" | "assistant"; content: string; timestamp: string }>;
  conversationState: ConversationState;
  pendingGoal: PendingGoal | null;
  showGoalOptions: boolean;
}

const HISTORY_KEY = "ai_create_history";
const MAX_HISTORY = 50;

function loadHistory(userId: string | null): SavedConversation[] {
  try {
    return JSON.parse(localStorage.getItem(userLsKey(HISTORY_KEY, userId)) || "[]");
  } catch {
    return [];
  }
}

function persistHistory(convs: SavedConversation[], userId: string | null) {
  localStorage.setItem(userLsKey(HISTORY_KEY, userId), JSON.stringify(convs.slice(0, MAX_HISTORY)));
}

function upsertConversation(conv: SavedConversation, userId: string | null) {
  const history = loadHistory(userId).filter(c => c.id !== conv.id);
  history.unshift(conv);
  persistHistory(history, userId);
}

function removeConversation(id: string, userId: string | null) {
  persistHistory(loadHistory(userId).filter(c => c.id !== id), userId);
}

const initialConversationState: ConversationState = {
  mode: "initial",
  step: 0,
  collectedData: {}
};

const initialMessage: Message = {
  id: "welcome",
  role: "assistant",
  content: `Welcome to AI Create! I'm here to help you get the most out of GoalVia.

What would you like to do today?

**1. Create** - I'll guide you through creating a new Goal, Habit, Task, Journal Entry, or Note with expert questions to help you define it properly.

**2. Learn How to Use GoalVia** - I'll walk you through the features and how to use them effectively.

**3. Learn Performance Improvement** - I'll share techniques and strategies for setting better goals, building stronger habits, and being more productive.

Just type a number (1, 2, or 3) or tell me what you'd like to do!`,
  timestamp: new Date()
};

function formatRelativeTime(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// --- DnD sub-components (defined outside main component for stable references) ---

function DraggableTask({ task, globalIdx }: { task: PendingTask; globalIdx: number }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `task-${globalIdx}`,
    data: { taskIdx: globalIdx },
  });
  return (
    <li
      ref={setNodeRef}
      className={`flex items-center gap-2 px-3 py-1.5 bg-background transition-opacity ${isDragging ? "opacity-30" : ""}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none shrink-0"
        title="Drag to move to another milestone"
      >
        <GripVertical className="w-3 h-3" />
      </button>
      <ListTodo className="w-3 h-3 text-muted-foreground shrink-0" />
      <span className="text-xs text-foreground flex-1">{task.title}</span>
      {task.priority && (
        <Badge
          variant={task.priority === "high" ? "destructive" : task.priority === "medium" ? "secondary" : "outline"}
          className="text-[10px] px-1 py-0 h-4 shrink-0"
        >
          {task.priority}
        </Badge>
      )}
    </li>
  );
}

function MilestoneDropZone({
  milestone,
  milestoneIdx,
  tasks,
}: {
  milestone: PendingMilestone;
  milestoneIdx: number;
  tasks: Array<{ task: PendingTask; globalIdx: number }>;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `milestone-${milestoneIdx}`,
    data: { milestoneIdx },
  });
  return (
    <div className={`border rounded-md overflow-hidden transition-colors ${isOver ? "border-primary bg-primary/5" : ""}`}>
      <div className="flex items-center gap-2 px-3 py-2 bg-primary/5 border-b">
        <Flag className="w-3 h-3 text-primary shrink-0" />
        <span className="text-xs font-semibold text-foreground flex-1">{milestone.title}</span>
        {milestone.dueDate && (
          <span className="text-[10px] text-muted-foreground flex items-center gap-0.5 shrink-0">
            <Calendar className="w-2.5 h-2.5" />
            {new Date(milestone.dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          </span>
        )}
      </div>
      <div ref={setNodeRef} className="min-h-[32px]">
        {tasks.length > 0 ? (
          <ul className="divide-y">
            {tasks.map(({ task, globalIdx }) => (
              <DraggableTask key={globalIdx} task={task} globalIdx={globalIdx} />
            ))}
          </ul>
        ) : (
          <p className="text-[10px] text-muted-foreground px-3 py-2 italic">
            {isOver ? "Drop here" : "No tasks assigned — drag tasks here"}
          </p>
        )}
      </div>
    </div>
  );
}

export default function AICreatePage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const userIdRef = useRef<string | null>(userId);
  const [, setLocation] = useLocation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [messages, setMessages] = useState<Message[]>([initialMessage]);
  const [inputValue, setInputValue] = useState("");
  const [conversationState, setConversationState] = useState<ConversationState>(initialConversationState);
  const [pendingGoal, setPendingGoal] = useState<PendingGoal | null>(null);
  const [showGoalOptions, setShowGoalOptions] = useState(false);

  // Local editable copy of tasks for drag-and-drop reassignment
  const [localTasks, setLocalTasks] = useState<PendingTask[]>([]);
  const [activeDragTask, setActiveDragTask] = useState<PendingTask | null>(null);

  // History state
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [history, setHistory] = useState<SavedConversation[]>([]);

  // Load history from user-scoped key once userId is known.
  useEffect(() => {
    userIdRef.current = userId;
    setHistory(loadHistory(userId));
    if (userId) {
      try { localStorage.removeItem(HISTORY_KEY); } catch {}
    }
  }, [userId]);
  const [showHistoryPanel, setShowHistoryPanel] = useState(true);

  // DnD sensors — require 8px movement to start drag (avoids accidental drags)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const { data: categories } = useQuery<Category[]>({
    queryKey: ["/api/categories?includeGoalsOnly=true"],
  });

  // Auto-save current conversation whenever messages change (if a session is active)
  const saveCurrentSession = useCallback((
    sessionId: string,
    msgs: Message[],
    state: ConversationState,
    pGoal: PendingGoal | null,
    showOpts: boolean
  ) => {
    const firstUserMsg = msgs.find(m => m.role === "user");
    if (!firstUserMsg) return;
    const title = firstUserMsg.content.trim().slice(0, 50) + (firstUserMsg.content.length > 50 ? "…" : "");
    const conv: SavedConversation = {
      id: sessionId,
      title,
      savedAt: new Date().toISOString(),
      messages: msgs.map(m => ({ ...m, timestamp: m.timestamp instanceof Date ? m.timestamp.toISOString() : m.timestamp })),
      conversationState: state,
      pendingGoal: pGoal,
      showGoalOptions: showOpts,
    };
    upsertConversation(conv, userIdRef.current);
    setHistory(loadHistory(userIdRef.current));
  }, []);

  useEffect(() => {
    if (!currentSessionId) return;
    const firstUserMsg = messages.find(m => m.role === "user");
    if (!firstUserMsg) return;
    saveCurrentSession(currentSessionId, messages, conversationState, pendingGoal, showGoalOptions);
  }, [messages, conversationState, pendingGoal, showGoalOptions, currentSessionId, saveCurrentSession]);

  // On first mount: if the user navigated back from Goal Simulation, restore the
  // confirmation panel so they can review milestones/tasks before creating.
  useEffect(() => {
    const stored = sessionStorage.getItem('pendingGoalSimulation');
    if (!stored) return;
    try {
      const data = JSON.parse(stored);
      if (data.source === 'ai-create' && data.title) {
        sessionStorage.removeItem('pendingGoalSimulation');
        setPendingGoal({
          title: data.title,
          description: data.description,
          targetDate: data.deadline || data.targetDate,
          category: data.category,
          progress: data.progress || 0,
          timeCost: data.timeCost,
          timeCostFrequency: data.timeCostFrequency,
          milestones: data.milestones || [],
          tasks: data.tasks || [],
        });
        setShowGoalOptions(true);
      }
    } catch {
      sessionStorage.removeItem('pendingGoalSimulation');
    }
  }, []);

  // Sync local tasks whenever pendingGoal changes (new goal proposed by AI).
  // Normalize: any task missing milestoneIndex gets explicitly assigned to 0
  // so the display fallback and the server handler always agree.
  useEffect(() => {
    if (pendingGoal?.tasks) {
      const hasMilestones = (pendingGoal.milestones?.length ?? 0) > 0;
      const normalized = pendingGoal.tasks.map(t => ({
        ...t,
        milestoneIndex: typeof t.milestoneIndex === 'number'
          ? t.milestoneIndex
          : (hasMilestones ? 0 : undefined),
      }));
      setLocalTasks(normalized);
    } else {
      setLocalTasks([]);
    }
  }, [pendingGoal]);

  const handleDragStart = (event: DragStartEvent) => {
    const taskIdx = event.active.data.current?.taskIdx as number;
    if (typeof taskIdx === "number") setActiveDragTask(localTasks[taskIdx] ?? null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragTask(null);
    const { active, over } = event;
    if (!over || !active) return;
    const taskIdx = active.data.current?.taskIdx as number;
    const targetMilestoneIdx = over.data.current?.milestoneIdx as number;
    if (typeof taskIdx !== "number" || typeof targetMilestoneIdx !== "number") return;
    setLocalTasks(prev => prev.map((t, i) => i === taskIdx ? { ...t, milestoneIndex: targetMilestoneIdx } : t));
  };

  const sendMessageMutation = useMutation({
    mutationFn: async (userMessage: string) => {
      const response = await apiRequest("POST", "/api/ai-create/chat", {
        message: userMessage,
        conversationState,
        messageHistory: messages.map(m => ({ role: m.role, content: m.content }))
      });
      return response.json();
    },
    onSuccess: (data) => {
      const assistantMessage: Message = {
        id: Date.now().toString(),
        role: "assistant",
        content: data.response,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
      
      if (data.newState) {
        setConversationState(data.newState);
      }

      setTimeout(() => inputRef.current?.focus(), 50);
      
      if (data.goalReady && data.goalData) {
        setPendingGoal(data.goalData);
        setShowGoalOptions(true);
      } else if (data.createdEntity && data.createdEntity.type !== 'goal') {
        toast({
          title: "Created Successfully!",
          description: `Your ${data.createdEntity.type} "${data.createdEntity.name}" has been created.`
        });
        const entityTypeMap: Record<string, string> = {
          habit: '/api/habits',
          task: '/api/tasks',
          journal: '/api/journal-entries',
          note: '/api/notes'
        };
        const queryKey = entityTypeMap[data.createdEntity.type] || `/api/${data.createdEntity.type}s`;
        queryClient.invalidateQueries({ queryKey: [queryKey] });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to get AI response",
        variant: "destructive"
      });
    }
  });

  const handleSendMessage = () => {
    if (!inputValue.trim() || sendMessageMutation.isPending) return;

    // Assign session ID on first user message
    let sessionId = currentSessionId;
    if (!sessionId) {
      sessionId = `session_${Date.now()}`;
      setCurrentSessionId(sessionId);
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    sendMessageMutation.mutate(inputValue.trim());
    setInputValue("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleQuickAction = (action: string) => {
    setInputValue(action);
    setTimeout(() => handleSendMessage(), 100);
  };

  const resetConversation = () => {
    setMessages([initialMessage]);
    setConversationState(initialConversationState);
    setInputValue("");
    setPendingGoal(null);
    setShowGoalOptions(false);
    setCurrentSessionId(null);
  };

  const loadConversation = (conv: SavedConversation) => {
    const msgs: Message[] = conv.messages.map(m => ({
      ...m,
      timestamp: new Date(m.timestamp)
    }));
    setMessages(msgs);
    setConversationState(conv.conversationState);
    setPendingGoal(conv.pendingGoal);
    setShowGoalOptions(conv.showGoalOptions);
    setCurrentSessionId(conv.id);
    setInputValue("");
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleDeleteHistory = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    removeConversation(id, userIdRef.current);
    setHistory(loadHistory(userIdRef.current));
    if (currentSessionId === id) {
      resetConversation();
    }
  };

  const createGoalMutation = useMutation({
    mutationFn: async (goalData: PendingGoal) => {
      const res = await apiRequest("POST", "/api/ai-create/create-goal-full", {
        title: goalData.title,
        description: goalData.description || null,
        deadline: goalData.targetDate || null,
        category: goalData.category || '',
        progress: 0,
        timeCost: goalData.timeCost || null,
        timeCostFrequency: goalData.timeCostFrequency || null,
        milestones: goalData.milestones || [],
        tasks: goalData.tasks || [],
      });
      return res.json() as Promise<{
        goal: { id: string; title: string };
        milestones: any[];
        tasks: any[];
        milestoneErrors: string[];
        taskErrors: string[];
      }>;
    },
    onSuccess: (data) => {
      const { goal, milestones, tasks, milestoneErrors, taskErrors } = data;
      const parts = [`Your goal "${goal.title}" has been created`];
      if (milestones.length > 0) parts.push(`with ${milestones.length} milestone${milestones.length !== 1 ? 's' : ''}`);
      if (tasks.length > 0) parts.push(`and ${tasks.length} task${tasks.length !== 1 ? 's' : ''}`);
      toast({
        title: "Goal Created!",
        description: parts.join(' ') + '.'
      });
      if (milestoneErrors.length > 0 || taskErrors.length > 0) {
        toast({
          title: "Some items failed",
          description: `Could not create: ${[...milestoneErrors, ...taskErrors].join(', ')}`,
          variant: "destructive"
        });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      setPendingGoal(null);
      setShowGoalOptions(false);
      resetConversation();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create goal",
        variant: "destructive"
      });
    }
  });

  const handleAddAsGoal = () => {
    if (pendingGoal) createGoalMutation.mutate({ ...pendingGoal, tasks: localTasks });
  };

  const handleSimulateGoal = () => {
    if (pendingGoal) {
      sessionStorage.setItem('pendingGoalSimulation', JSON.stringify({ ...pendingGoal, tasks: localTasks, source: "ai-create" }));
      setLocation("/goal-simulation");
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className={`w-full max-w-full py-4 md:py-6 px-4 md:px-6 flex gap-4 ${showGoalOptions ? "min-h-[calc(100vh-4rem)] items-start" : "h-[calc(100vh-4rem)]"}`}>

      {/* Main chat panel */}
      <div className="flex-1 min-w-0 flex flex-col">
        <Card className={`flex flex-col ${showGoalOptions ? "" : "flex-1 overflow-hidden"}`}>
          <CardHeader className="pb-4 shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Wand2 className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <Wand2 className="w-5 h-5" />
                    AI Create
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Your personal assistant for creating goals, habits, tasks, journals, and notes
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetConversation}
                  data-testid="button-reset-conversation"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  New Chat
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowHistoryPanel(p => !p)}
                  title={showHistoryPanel ? "Hide history" : "Show history"}
                >
                  {showHistoryPanel ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardContent className={`flex flex-col p-4 pt-0 ${showGoalOptions ? "" : "flex-1 overflow-hidden"}`}>
            {conversationState.mode === "initial" && (
              <div className="flex gap-2 mb-4 flex-wrap shrink-0">
                <Button variant="outline" size="sm" onClick={() => handleQuickAction("I want to create a goal")} data-testid="button-quick-goal">
                  <Target className="w-4 h-4 mr-2" />Create Goal
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleQuickAction("I want to create a habit")} data-testid="button-quick-habit">
                  <CheckSquare className="w-4 h-4 mr-2" />Create Habit
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleQuickAction("I want to create a task")} data-testid="button-quick-task">
                  <ListTodo className="w-4 h-4 mr-2" />Create Task
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleQuickAction("I want to write a journal entry")} data-testid="button-quick-journal">
                  <FileText className="w-4 h-4 mr-2" />Write Journal
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleQuickAction("I want to create a note")} data-testid="button-quick-note">
                  <StickyNote className="w-4 h-4 mr-2" />Create Note
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleQuickAction("Teach me how to use the system")} data-testid="button-quick-learn">
                  <BookOpen className="w-4 h-4 mr-2" />Learn System
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleQuickAction("Help me improve my performance")} data-testid="button-quick-performance">
                  <Lightbulb className="w-4 h-4 mr-2" />Performance Tips
                </Button>
              </div>
            )}

            <ScrollArea className={`pr-4 mb-4 ${showGoalOptions ? "h-[35vh]" : "flex-1"}`} data-testid="chat-messages">
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex gap-3 ${message.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    {message.role === "assistant" && (
                      <Avatar className="w-8 h-8 shrink-0">
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          <Bot className="w-4 h-4" />
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <div
                      className={`max-w-[80%] rounded-lg p-4 ${
                        message.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"
                      }`}
                      data-testid={`message-${message.role}-${message.id}`}
                    >
                      <div className="whitespace-pre-wrap text-sm prose prose-sm dark:prose-invert max-w-none">
                        {message.content.split('\n').map((line, i) => {
                          if (line.startsWith('**') && line.endsWith('**')) {
                            return <p key={i} className="font-semibold my-1">{line.replace(/\*\*/g, '')}</p>;
                          }
                          if (line.match(/^\*\*\d+\./)) {
                            return <p key={i} className="font-medium my-1">{line.replace(/\*\*/g, '')}</p>;
                          }
                          return <p key={i} className="my-1">{line}</p>;
                        })}
                      </div>
                    </div>
                    {message.role === "user" && (
                      <Avatar className="w-8 h-8 shrink-0">
                        <AvatarFallback className="bg-secondary">
                          <User className="w-4 h-4" />
                        </AvatarFallback>
                      </Avatar>
                    )}
                  </div>
                ))}
                {sendMessageMutation.isPending && (
                  <div className="flex gap-3 justify-start">
                    <Avatar className="w-8 h-8 shrink-0">
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        <Bot className="w-4 h-4" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="bg-muted rounded-lg p-4">
                      <Loader2 className="w-4 h-4 animate-spin" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {showGoalOptions && pendingGoal && (
              <div className="mb-4 border rounded-lg bg-muted/50 overflow-hidden shrink-0">
                <div className="p-4 border-b bg-background/50">
                  <div className="flex items-center gap-2 mb-1">
                    <Target className="w-4 h-4 text-primary" />
                    <p className="text-sm font-semibold">{pendingGoal.title}</p>
                  </div>
                  {pendingGoal.description && (
                    <p className="text-xs text-muted-foreground line-clamp-2">{pendingGoal.description}</p>
                  )}
                </div>

                {pendingGoal.milestones && pendingGoal.milestones.length > 0 && (
                  <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                    <div className="px-3 pt-2 pb-1">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1 mb-2">
                        <Flag className="w-3 h-3" />{pendingGoal.milestones.length} Milestones
                        {localTasks.length > 0 && <span className="ml-1">· {localTasks.length} Tasks</span>}
                        <span className="ml-auto text-[10px] font-normal normal-case">drag tasks to reassign</span>
                      </p>
                    </div>
                    <div className="overflow-y-auto" style={{ maxHeight: "48rem" }}>
                      <div className="px-3 pb-3 space-y-2">
                        {pendingGoal.milestones.map((m, mi) => {
                          const milestoneTasks = localTasks
                            .map((t, i) => ({ task: t, globalIdx: i }))
                            .filter(({ task }) => task.milestoneIndex === mi || (mi === 0 && typeof task.milestoneIndex !== "number"));
                          return (
                            <MilestoneDropZone key={mi} milestone={m} milestoneIdx={mi} tasks={milestoneTasks} />
                          );
                        })}
                      </div>
                    </div>
                    <DragOverlay>
                      {activeDragTask && (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-background border rounded-md shadow-lg text-xs pointer-events-none">
                          <GripVertical className="w-3 h-3 text-muted-foreground" />
                          <ListTodo className="w-3 h-3 text-muted-foreground" />
                          <span className="flex-1">{activeDragTask.title}</span>
                          {activeDragTask.priority && (
                            <Badge
                              variant={activeDragTask.priority === "high" ? "destructive" : activeDragTask.priority === "medium" ? "secondary" : "outline"}
                              className="text-[10px] px-1 py-0 h-4"
                            >
                              {activeDragTask.priority}
                            </Badge>
                          )}
                        </div>
                      )}
                    </DragOverlay>
                  </DndContext>
                )}

                <div className="p-3 border-t flex gap-2">
                  <Button onClick={handleAddAsGoal} disabled={createGoalMutation.isPending} className="flex-1" data-testid="button-add-as-goal">
                    {createGoalMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                    Create Goal
                  </Button>
                  <Button variant="outline" onClick={handleSimulateGoal} disabled={createGoalMutation.isPending} className="flex-1" data-testid="button-simulate-goal">
                    <FlaskConical className="w-4 h-4 mr-2" />Simulate First
                  </Button>
                </div>
              </div>
            )}

            <div className="flex gap-2 items-end shrink-0">
              <Textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Type your message... (Shift+Enter for new line)"
                disabled={sendMessageMutation.isPending || showGoalOptions}
                className="flex-1 min-h-[44px] max-h-[200px] resize-none"
                rows={1}
                data-testid="input-chat-message"
              />
              <Button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || sendMessageMutation.isPending || showGoalOptions}
                className="h-[44px]"
                data-testid="button-send-message"
              >
                {sendMessageMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* History panel */}
      {showHistoryPanel && (
        <div className={`w-72 shrink-0 hidden md:flex flex-col ${showGoalOptions ? "sticky top-4 self-start" : ""}`}>
          <Card className={`flex flex-col ${showGoalOptions ? "h-[calc(100vh-5rem)] overflow-hidden" : "flex-1 overflow-hidden"}`}>
            <CardHeader className="pb-3 shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Chat History
                </CardTitle>
                {history.length > 0 && (
                  <span className="text-xs text-muted-foreground">{history.length} chat{history.length !== 1 ? 's' : ''}</span>
                )}
              </div>
            </CardHeader>
            <CardContent className="flex flex-col flex-1 overflow-hidden p-0">
              {history.length === 0 ? (
                <div className="flex flex-col items-center justify-center flex-1 px-4 text-center gap-2">
                  <MessageSquare className="w-8 h-8 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">No saved chats yet.</p>
                  <p className="text-xs text-muted-foreground">Start a conversation and it will appear here automatically.</p>
                </div>
              ) : (
                <ScrollArea className="flex-1">
                  <div className="space-y-1 p-2">
                    {history.map((conv) => (
                      <div
                        key={conv.id}
                        onClick={() => loadConversation(conv)}
                        className={`group flex items-start gap-2 p-3 rounded-lg cursor-pointer transition-colors hover:bg-muted/70 ${
                          currentSessionId === conv.id ? "bg-primary/10 border border-primary/20" : "border border-transparent"
                        }`}
                      >
                        <MessageSquare className={`w-4 h-4 mt-0.5 shrink-0 ${currentSessionId === conv.id ? 'text-primary' : 'text-muted-foreground'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium leading-snug line-clamp-2 break-words">
                            {conv.title}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {formatRelativeTime(conv.savedAt)}
                          </p>
                          {(conv.conversationState.mode !== "initial") && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 mt-1">
                              {conv.conversationState.mode.replace('creating_', '').replace('learning_', 'learn: ')}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <ChevronRight className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                          <button
                            onClick={(e) => handleDeleteHistory(e, conv.id)}
                            className="p-1 rounded hover:bg-destructive/10 hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Delete this chat"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
